import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import emailjs from '@emailjs/browser';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import MessageIcon from '@mui/icons-material/Message';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Link } from 'react-router-dom';
import logo from '../assets/BCL_nobg_crop.png';
import './ContactForm.css';

const ContactForm = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Replace these with your actual EmailJS credentials
  const EMAILJS_SERVICE_ID = 'service_vjbmsg8';
  const EMAILJS_TEMPLATE_ID = 'template_g9tkwui';
  const EMAILJS_PUBLIC_KEY = 'Y-I4CX8cZFF7BNLwF';

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    // Get current time in a readable format
    const currentTime = new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // Prepare template parameters
    const templateParams = {
      from_name: `${data.firstName} ${data.lastName}`,
      from_email: data.email,
      organization: data.organization || 'Not provided',
      message: data.message,
      current_time: currentTime,
      to_name: 'BioCircuit Labs Team',
    };

    try {
      // Send email using EmailJS
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('Email sent successfully:', response);
      setSnackbarMessage('Thank you for reaching out! We\'ll get back to you soon.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      reset(); // Clear the form
    } catch (error) {
      console.error('Email sending failed:', error);
      setSnackbarMessage('Oops! Something went wrong. Please try again or email us directly.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box className="contact-section" sx={{ py: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%)', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              fontWeight: 700,
              color: '#51A8B1',
              mb: 1.5,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Get In Touch
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              color: '#666',
              mb: 6,
              fontSize: '1.125rem',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Let's discuss how we can transform your healthcare operations
          </Typography>
        </motion.div>

        {/* Main Content - Side by Side using Flexbox */}
        <Box 
          sx={{ 
            maxWidth: '1400px', 
            mx: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            alignItems: 'stretch'
          }}
        >
          {/* Left Side - Contact Form */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <Paper 
                elevation={3}
                sx={{ 
                  p: { xs: 4, md: 6 },
                  borderRadius: 4,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9fafb 100%)',
                  border: '1px solid rgba(81, 168, 177, 0.1)',
                  boxShadow: '0 20px 60px rgba(81, 168, 177, 0.12)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 700,
                    color: '#51A8B1',
                    mb: 1,
                    fontSize: { xs: '1.75rem', md: '2rem' },
                  }}
                >
                  Send us a Message
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: '#888',
                    mb: 4,
                    fontSize: '0.95rem',
                  }}
                >
                  Fill out the form below and we'll get back to you shortly
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* First Name & Last Name Row */}
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        variant="outlined"
                        {...register('firstName', { required: 'First name is required' })}
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        disabled={isSubmitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2.5,
                            backgroundColor: '#ffffff',
                            transition: 'all 0.3s ease',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                              borderWidth: '2px',
                            },
                            '&:hover': {
                              backgroundColor: '#fafbfc',
                              '& fieldset': {
                                borderColor: '#51A8B1',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#ffffff',
                              boxShadow: '0 0 0 4px rgba(81, 168, 177, 0.1)',
                              '& fieldset': {
                                borderColor: '#51A8B1',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontFamily: 'Quicksand, sans-serif',
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: '#51A8B1',
                              fontWeight: 600,
                            },
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Last Name"
                        variant="outlined"
                        {...register('lastName', { required: 'Last name is required' })}
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        disabled={isSubmitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2.5,
                            backgroundColor: '#ffffff',
                            transition: 'all 0.3s ease',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                              borderWidth: '2px',
                            },
                            '&:hover': {
                              backgroundColor: '#fafbfc',
                              '& fieldset': {
                                borderColor: '#51A8B1',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#ffffff',
                              boxShadow: '0 0 0 4px rgba(81, 168, 177, 0.1)',
                              '& fieldset': {
                                borderColor: '#51A8B1',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontFamily: 'Quicksand, sans-serif',
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: '#51A8B1',
                              fontWeight: 600,
                            },
                          },
                        }}
                      />
                    </Box>

                    {/* Email */}
                    <TextField
                      fullWidth
                      label="Email Address"
                      variant="outlined"
                      type="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          backgroundColor: '#ffffff',
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                            borderWidth: '2px',
                          },
                          '&:hover': {
                            backgroundColor: '#fafbfc',
                            '& fieldset': {
                              borderColor: '#51A8B1',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 0 0 4px rgba(81, 168, 177, 0.1)',
                            '& fieldset': {
                              borderColor: '#51A8B1',
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontFamily: 'Quicksand, sans-serif',
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: '#51A8B1',
                            fontWeight: 600,
                          },
                        },
                      }}
                    />

                    {/* Organization */}
                    <TextField
                      fullWidth
                      label="Organization"
                      variant="outlined"
                      {...register('organization')}
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          backgroundColor: '#ffffff',
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                            borderWidth: '2px',
                          },
                          '&:hover': {
                            backgroundColor: '#fafbfc',
                            '& fieldset': {
                              borderColor: '#51A8B1',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 0 0 4px rgba(81, 168, 177, 0.1)',
                            '& fieldset': {
                              borderColor: '#51A8B1',
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontFamily: 'Quicksand, sans-serif',
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: '#51A8B1',
                            fontWeight: 600,
                          },
                        },
                      }}
                    />

                    {/* Message */}
                    <TextField
                      fullWidth
                      label="Your Message"
                      variant="outlined"
                      multiline
                      rows={4}
                      {...register('message', { required: 'Message is required' })}
                      error={!!errors.message}
                      helperText={errors.message?.message}
                      disabled={isSubmitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                            <MessageIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          backgroundColor: '#ffffff',
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                            borderWidth: '2px',
                          },
                          '&:hover': {
                            backgroundColor: '#fafbfc',
                            '& fieldset': {
                              borderColor: '#51A8B1',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 0 0 4px rgba(81, 168, 177, 0.1)',
                            '& fieldset': {
                              borderColor: '#51A8B1',
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontFamily: 'Quicksand, sans-serif',
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: '#51A8B1',
                            fontWeight: 600,
                          },
                        },
                      }}
                    />

                    {/* Submit Button */}
                    <motion.div
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        fullWidth
                        disabled={isSubmitting}
                        sx={{
                          background: 'linear-gradient(135deg, #51A8B1 0%, #459199 100%)',
                          color: 'white',
                          padding: '16px 32px',
                          fontFamily: 'Barlow, sans-serif',
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          borderRadius: 2.5,
                          textTransform: 'none',
                          boxShadow: '0 8px 24px rgba(81, 168, 177, 0.3)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #459199 0%, #3a7d85 100%)',
                            boxShadow: '0 12px 32px rgba(81, 168, 177, 0.4)',
                            transform: 'translateY(-2px)',
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #a0a0a0 0%, #888888 100%)',
                            color: 'white',
                          },
                        }}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </motion.div>
                  </Box>
                </form>
              </Paper>
            </motion.div>
          </Box>

          {/* Right Side - Lottie Animation */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: { xs: 2, md: 4 },
                }}
              >
                {/* Lottie Animation Container */}
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '750px',
                    height: 'auto',
                    mb: 4,
                    filter: 'drop-shadow(0 15px 40px rgba(81, 168, 177, 0.2))',
                  }}
                >
                  <DotLottieReact
                    src="https://lottie.host/7206b2d5-e6f0-4b78-a4cb-4686bfa65cd8/SOOMc4CXV5.lottie"
                    loop
                    autoplay
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </Box>

                {/* Info Card Below Animation */}
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f0f9fa 100%)',
                    border: '2px solid rgba(81, 168, 177, 0.15)',
                    textAlign: 'center',
                    maxWidth: '500px',
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Quicksand, sans-serif',
                      fontWeight: 700,
                      color: '#51A8B1',
                      mb: 2,
                      fontSize: { xs: '1.75rem', md: '2rem' },
                    }}
                  >
                    We're Here to Help
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#666',
                      lineHeight: 1.8,
                      mb: 3,
                      fontSize: '1rem',
                    }}
                  >
                    Our team responds to all inquiries within 24 hours
                  </Typography>

                  {/* Contact Info */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(81, 168, 177, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <EmailIcon sx={{ color: '#51A8B1', fontSize: '1.3rem' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Quicksand, sans-serif',
                          color: '#555',
                          fontSize: '1rem',
                          fontWeight: 500,
                        }}
                      >
                        info@biocircuitlabs.com
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(81, 168, 177, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PhoneIcon sx={{ color: '#51A8B1', fontSize: '1.3rem' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Quicksand, sans-serif',
                          color: '#555',
                          fontSize: '1rem',
                          fontWeight: 500,
                        }}
                      >
                        +91 94141 74048
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </motion.div>
          </Box>
        </Box>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbarSeverity}
            sx={{ 
              width: '100%',
              borderRadius: 2.5,
              fontFamily: 'Quicksand, sans-serif',
              fontWeight: 600,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>

      {/* Footer Section */}
      <Box 
        component="footer" 
        sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(81, 168, 177, 0.1)',
          pt: 8,
          pb: 4,
          mt: 12
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
            {/* Logo and Company Info */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={logo} 
                    alt="BioCircuit Labs" 
                    style={{ height: '80px', marginRight: '15px' }}
                  />
                </motion.div>
                <Box>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#51A8B1',
                      fontWeight: 700,
                    }}
                  >
                    BioCircuit
                  </Typography>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      fontFamily: 'Barlow, sans-serif',
                      color: '#A8CF45',
                      fontWeight: 700,
                      marginLeft: '8px'
                    }}
                  >
                    LABS
                  </Typography>
                </Box>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontFamily: 'Quicksand, sans-serif',
                  mb: 2,
                  lineHeight: 1.7
                }}
              >
                Transforming healthcare operations through innovative technology solutions.
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontFamily: 'Quicksand, sans-serif',
                  mb: 2,
                  lineHeight: 1.7
                }}
              >
                GSTIN : 08ALWPP4265B1ZQ
              </Typography>
              {/* Social Media Icons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                    <a 
                    href="https://www.linkedin.com/company/biocircuit-labs"
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }} 
                    >
                    <IconButton 
                        sx={{ 
                        color: '#51A8B1',
                        '&:hover': { backgroundColor: 'rgba(81, 168, 177, 0.1)' }
                        }}
                    >
                        <LinkedInIcon />
                    </IconButton>
                    </a>
                </motion.div>
                </Box>
            </Box>

            {/* Quick Links */}
            <Box sx={{ flex: 0.6 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { text: 'Home', path: '/' },
                  { text: 'Careers', path: '/careers' },
                  { text: 'Contact', path: '/contact' }
                ].map((item) => (
                  <motion.div
                    key={item.text}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={item.path}
                      style={{ 
                        textDecoration: 'none',
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#51A8B1'}
                      onMouseLeave={(e) => e.target.style.color = '#666'}
                    >
                      {item.text}
                    </Link>
                  </motion.div>
                ))}
              </Box>
            </Box>

            {/* Legal */}
            <Box sx={{ flex: 0.6 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { text: 'Privacy Policy', path: '/privacy-policy' },
                  { text: 'Terms & Conditions', path: '/terms-conditions' }
                ].map((item) => (
                  <motion.div
                    key={item.text}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={item.path}
                      style={{ 
                        textDecoration: 'none',
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#51A8B1'}
                      onMouseLeave={(e) => e.target.style.color = '#666'}
                    >
                      {item.text}
                    </Link>
                  </motion.div>
                ))}
              </Box>
            </Box>

            {/* Contact Info */}
            <Box sx={{ flex: 0.8 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Contact Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                  <Typography
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#666',
                      fontSize: '0.95rem'
                    }}
                  >
                    info@biocircuitlabs.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PhoneIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                  <Typography
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#666',
                      fontSize: '0.95rem'
                    }}
                  >
                    +91 94141 74048
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Bottom Bar */}
          <Divider sx={{ my: 4, borderColor: 'rgba(81, 168, 177, 0.1)' }} />
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                textAlign: 'center'
              }}
            >
              © {new Date().getFullYear()} BioCircuit Labs. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactForm;